import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MAX = 10;
const POLL_INTERVAL = 2000;

export default function RetoucheStatus({ kind = "success" }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", transaction: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (kind !== "success") { setState({ status: "cancelled" }); return; }
    if (!sessionId) { setState({ status: "missing" }); return; }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/retouch/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ status: "paid", transaction: data.transaction }); return; }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
        attempts.current += 1;
        if (attempts.current >= POLL_MAX) { setState({ status: "timeout", transaction: data.transaction }); return; }
        setTimeout(tick, POLL_INTERVAL);
      } catch {
        attempts.current += 1;
        if (attempts.current >= POLL_MAX) setState({ status: "error" });
        else setTimeout(tick, POLL_INTERVAL);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, kind]);

  return (
    <div data-testid="page-retouche-status" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 text-center">
      {state.status === "polling" && (<><Loader2 size={48} className="mx-auto animate-spin text-[color:var(--papicoo-gold)]" /><h1 className="font-display text-3xl mt-6">{T("On confirme ta commande…")}</h1></>)}
      {state.status === "paid" && (
        <>
          <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
          <h1 className="font-display text-3xl mt-6">{T("Commande confirmée 🌹")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">
            <strong>{T("Jërëjëf.")}</strong> {T("Envoie tes photos à")} <a href="mailto:papicoo@papicoo.fr" className="papicoo-link-underline">{T("papicoo@papicoo.fr")}</a> {T("avec en objet")} <em>« Retouche {state.transaction?.metadata?.buyer_name || ""} »</em>.
          </p>
          {state.transaction?.metadata && (
            <div className="mt-6 papicoo-card p-5 text-left text-sm space-y-1">
              <div>{T("Pack :")} <strong>{state.transaction.metadata.package_label}</strong></div>
              <div>{T("Photos :")} <strong>{state.transaction.metadata.photos}</strong></div>
              <div>{T("Style :")} <strong>{state.transaction.metadata.style}</strong></div>
              <div>{T("Total :")} <strong>{Number(state.transaction.amount_eur || 0).toFixed(2)} €</strong></div>
            </div>
          )}
        </>
      )}
      {state.status === "cancelled" && (<><XCircle size={56} className="mx-auto text-amber-600" /><h1 className="font-display text-3xl mt-6">{T("Commande annulée")}</h1><p className="text-[color:var(--papicoo-muted)] mt-3">{T("Aucun débit. Reviens quand tu veux.")}</p></>)}
      {(state.status === "expired" || state.status === "missing" || state.status === "error") && (<><XCircle size={56} className="mx-auto text-red-600" /><h1 className="font-display text-3xl mt-6">{T("Erreur de vérification")}</h1></>)}
      {state.status === "timeout" && (<><Loader2 size={48} className="mx-auto text-amber-600" /><h1 className="font-display text-3xl mt-6">{T("Vérification en cours")}</h1></>)}
      <Link to="/retouche" data-testid="back-retouche" className="mt-8 inline-flex items-center gap-2 text-sm papicoo-link-underline"><ArrowLeft size={14} /> {T("Retour aux packs")}</Link>
    </div>
  );
}
